/**
 * Maintenance Ticket Tags hooks using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useMaintenanceTicketTagsRelayDirectQuery as UseMaintenanceTicketTagsRelayDirectQuery,
  MaintenanceTicketTagFilterInput,
  MaintenanceTicketTagSortInput,
} from './__generated__/useMaintenanceTicketTagsRelayDirectQuery.graphql';
import type { useMaintenanceTicketTagsRelayByIdQuery } from './__generated__/useMaintenanceTicketTagsRelayByIdQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Direct query for maintenance ticket tags list
 */
const MaintenanceTicketTagsDirectQuery = graphql`
  query useMaintenanceTicketTagsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: MaintenanceTicketTagFilterInput
    $sort: [MaintenanceTicketTagSortInput!]
  ) {
    listMaintenanceTicketTags(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          createdOn
          archivedOn
          organizationId
          partnerId
          systemId
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for single maintenance ticket tag by ID
 */
const MaintenanceTicketTagByIdQuery = graphql`
  query useMaintenanceTicketTagsRelayByIdQuery($id: ID!) {
    getMaintenanceTicketTag(id: $id) {
      id
      name
      createdOn
      archivedOn
      organizationId
      partnerId
      systemId
      system {
        id
        oldId
        name
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { MaintenanceTicketTagFilterInput, MaintenanceTicketTagSortInput };

export interface TagSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface MaintenanceTicketTagGraphQL {
  id: string;
  name: string;
  createdOn: string;
  archivedOn?: string | null;
  organizationId?: string | null;
  partnerId?: string | null;
  systemId?: string | null;
  system?: TagSystemGraphQL | null;
}

export interface MaintenanceTagQueryFilter {
  systemId?: string;
  organizationId?: string;
  includeArchived?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching a single tag by ID (Suspense)
 */
export function useMaintenanceTicketTagByIdRelay(tagId: string) {
  const data = useLazyLoadQuery<useMaintenanceTicketTagsRelayByIdQuery>(
    MaintenanceTicketTagByIdQuery,
    { id: tagId }
  );

  return data.getMaintenanceTicketTag as MaintenanceTicketTagGraphQL | null;
}

/**
 * Hook that provides React Query-like interface for maintenance ticket tags data.
 * Does NOT use Suspense.
 */
export function useMaintenanceTicketTagsRelayQuery(
  filter?: MaintenanceTagQueryFilter,
  options?: {
    limit?: number;
    sort?: MaintenanceTicketTagSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<MaintenanceTicketTagGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 250;
  const sort = useMemo(
    () =>
      options?.sort ?? [
        { field: 'NAME' as const, order: 'ASC' as const },
      ],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildTagRelayFilter(filter);
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (builtFilter) {
          variables.filter = builtFilter;
        }

        const result =
          await fetchQuery<UseMaintenanceTicketTagsRelayDirectQuery>(
            relayEnvironment,
            MaintenanceTicketTagsDirectQuery,
            variables
          ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listMaintenanceTicketTags?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as MaintenanceTicketTagGraphQL[]);
          setCount(result.listMaintenanceTicketTags?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch maintenance ticket tags')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function buildTagRelayFilter(
  filter?: MaintenanceTagQueryFilter
): MaintenanceTicketTagFilterInput | undefined {
  if (!filter) {
    // Default: active only
    return { archivedOn: { isNil: true } };
  }

  const relayFilter: MaintenanceTicketTagFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }

  // Default to active-only unless explicitly including archived
  if (!filter.includeArchived) {
    relayFilter.archivedOn = { isNil: true };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildTagRelayFilter };
